import React, { ChangeEvent, DragEvent, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { __t } from '../../../utils/t';
import { Icon } from '../../Icon';
import { useAppDispatch } from '../../../hooks';
import { appActions } from '../../../redux/slices/appSlice';
import { getFilePreviewUrl } from '../../../utils';

import './styles.scss';

export interface FileToUpload {
    id: string;
    file: File;
}

export interface UploadedFile {
    id: number;
    url: string;
}

interface Props {
    title: string;
    filesToUpload: FileToUpload[];
    uploadedFiles: UploadedFile[];
    accept?: string[];
    fullInputMaxWidth?: string;
    maxFileSize?: number;
    maxFiles?: number;
    setFiles: (files: FileToUpload[]) => void;
    removeFileToUpload: (id: string) => void;
    removeUploadedFile: (id: number) => void;
}

export const FileUpload = ({
    title,
    accept,
    filesToUpload,
    uploadedFiles,
    fullInputMaxWidth,
    maxFileSize,
    maxFiles,
    setFiles,
    removeFileToUpload,
    removeUploadedFile,
}: Props) => {
    const dispatch = useAppDispatch();
    const [dragActive, setDragActive] = useState(false);

    const uploadedFilesPreviews = useMemo(
        () =>
            uploadedFiles.map(({ id, url }) => (
                <div className='app-file-upload-preview-item' key={id}>
                    <img className='app-file-upload-preview-item__file' src={url} />
                    <button
                        className='app-file-upload-preview-item__remove-button'
                        onClick={() => removeUploadedFile(id)}
                    >
                        <Icon icon='xmark-solid' size='16px' />
                    </button>
                </div>
            )),
        [uploadedFiles],
    );

    const filesToUploadPreviews = useMemo(
        () =>
            filesToUpload.map(({ id, file }) => (
                <div className='app-file-upload-preview-item' key={id}>
                    <img className='app-file-upload-preview-item__file' src={getFilePreviewUrl(file)} />
                    <button
                        className='app-file-upload-preview-item__remove-button'
                        onClick={() => removeFileToUpload(id)}
                    >
                        <Icon icon='xmark-solid' size='16px' />
                    </button>
                </div>
            )),
        [filesToUpload],
    );

    const hasPreviews = useMemo(() => !!filesToUpload.length || !!uploadedFiles.length, [uploadedFiles, filesToUpload]);

    const handleFiles = (fileList: FileList | null): void => {
        if (!fileList) return;

        const filesToAdd = [...filesToUpload];
        for (let i = 0; i < fileList.length; i++) {
            const file = fileList[i];
            const validationError = validateFile(file, filesToAdd.length + uploadedFiles.length);
            if (validationError) {
                dispatch(
                    appActions.enqueueSnackbar({
                        id: uuid(),
                        message: validationError,
                        title: file.name,
                        icon: 'triangle-exclamation-duonote-red',
                        iconColor: 'danger',
                    }),
                );
            } else {
                filesToAdd.push({
                    id: uuid(),
                    file: fileList[i],
                });
            }
        }

        setFiles(filesToAdd);
    };

    const validateFile = (file: File, totalFilesCount: number): string => {
        if (accept?.length && !accept.includes(file.type)) {
            return `${__t('Тип файла не поддерживается. Поддерживаемые типы:')} ${accept.join()}`;
        }

        if (maxFileSize && file.size > maxFileSize) {
            return `${__t('Нельзя загружать файлы более')} ${maxFileSize / 1000000}Mb`;
        }

        if (maxFiles && totalFilesCount >= maxFiles) {
            return `${__t('Нельзя загрузить более')} ${maxFiles} ${__t('файлов')}`;
        }

        return '';
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
        e.target.value = '';
    };

    const handleDrag = function (e: DragEvent<HTMLElement>) {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
            return;
        }
        if (e.type === 'dragleave') {
            setDragActive(false);
            return;
        }
    };

    const handleDrop = function (e: DragEvent<HTMLElement>) {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        handleFiles(e.dataTransfer.files);
    };

    const input = useMemo(() => {
        const totalFilesCount = filesToUpload.length + uploadedFiles.length;
        if (maxFiles && totalFilesCount >= maxFiles) {
            return null;
        }

        return (
            <label
                htmlFor='app-file-upload'
                className={`app-file-upload-input ${hasPreviews ? 'app-file-upload-input--small' : ''} ${
                    dragActive ? 'app-file-upload-input--drag-active' : ''
                }`}
                style={{
                    maxWidth: fullInputMaxWidth || '100%',
                }}
                onDragEnter={handleDrag}
            >
                <input
                    id='app-file-upload'
                    multiple
                    type='file'
                    className='app-file-upload-input__hidden-input'
                    onChange={handleChange}
                />
                <Icon icon='images-thin' size='48' className='app-file-upload-input__icon' />
                {!hasPreviews && (
                    <>
                        <p className='app-file-upload-input__title'>{title}</p>
                        <p className='app-file-upload-input__helper-text'>
                            {__t('Перетащите сюда фото или загрузите с устройства')}
                        </p>
                    </>
                )}
                {dragActive && (
                    <div
                        className='app-file-upload-input__drag-overlay'
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                    ></div>
                )}
            </label>
        );
    }, [
        filesToUpload,
        uploadedFiles,
        maxFiles,
        hasPreviews,
        dragActive,
        fullInputMaxWidth,
        title,
        handleDrag,
        handleDrop,
        handleChange,
    ]);

    return (
        <div className='app-file-upload'>
            <div className={`${hasPreviews ? 'app-file-upload__preview-wrap' : ''}`}>
                {uploadedFilesPreviews}
                {filesToUploadPreviews}
                {input}
            </div>
        </div>
    );
};
